import React, { useState, useEffect } from "react";
import axios from "axios";
import JobListingTemplate from "./JobListingTemplate";
import { API_BASE_URL } from "../../../config/api";
import Footer from "../molecules/Footer";

/* =============== helpers =============== */

const getAuthHeaders = () => {
  const token =
    localStorage.getItem("access_token") ||
    localStorage.getItem("token") ||
    localStorage.getItem("authToken");

  return token
    ? {
        Authorization: `Bearer ${token}`,
        Accept: "application/json",
      }
    : {
        Accept: "application/json",
      };
};

/** Build a safe /api/* URL regardless of how API_BASE_URL is set */
const buildApiUrl = (path = "/") => {
  const raw = (API_BASE_URL || "").trim();
  const baseNoSlash = raw.replace(/\/+$/, ""); // remove trailing slashes
  const baseWithApi = /\/api$/i.test(baseNoSlash) ? baseNoSlash : `${baseNoSlash}/api`;
  const safePath = path.startsWith("/") ? path : `/${path}`;
  return `${baseWithApi}${safePath}`;
};

const extractJobsArray = (res) => {
  const r = res?.data ?? res ?? null;

  const candidates = [
    r?.data?.data,
    r?.data?.rows,
    r?.data?.items,
    r?.data?.jobs,
    r?.data?.results,
    r?.data?.content,
    r?.content,
    r?.data,
    r?.rows,
    r?.items,
    r?.jobs,
    r?.results,
    Array.isArray(r) ? r : null,
  ].filter(Array.isArray);

  if (candidates[0]) return candidates[0];

  // extra prod-ish shapes
  if (Array.isArray(r?.payload?.jobs)) return r.payload.jobs;
  if (Array.isArray(r?.payload?.items)) return r.payload.items;
  if (Array.isArray(r?.data?.jobs?.content)) return r.data.jobs.content;
  if (Array.isArray(r?.data?.jobs?.items)) return r.data.jobs.items;

  return [];
};

const normalizeJob = (raw) => {
  if (!raw || typeof raw !== "object") return null;

  const id =
    raw.jobId ??
    raw.id ??
    raw._id ??
    raw.job_id ??
    raw.jobid ??
    raw.requestId ??
    null;

  const title =
    raw.positionTitle ??
    raw.title ??
    raw.position ??
    raw.jobTitle ??
    raw.roleTitle ??
    "";

  return {
    jobId: id,
    positionTitle: title,
    ...raw,
  };
};

/* =============== component =============== */

const JobListingPage = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errMsg, setErrMsg] = useState("");

  useEffect(() => {
    let mounted = true;

    (async () => {
      try {
        setLoading(true);

        // ✅ Safe URL no matter what API_BASE_URL is ("/api", "/", full origin, etc.)
        const url = buildApiUrl("/jobs");
        const headers = getAuthHeaders();

        const res = await axios.get(url, {
          headers,
          timeout: 15000,
          withCredentials: false,
        });

        // Debug only in non-prod builds
        if (process.env.NODE_ENV !== "production") {
          // eslint-disable-next-line no-console
          console.log("[MTN-Jobs] GET", url);
          // eslint-disable-next-line no-console
          console.log("[MTN-Jobs] raw response:", res.data);
          if (typeof window !== "undefined") {
            window.__MTN_JOBS_DEBUG__ = {
              fetchedAt: new Date().toISOString(),
              url,
              headers,
              raw: res.data,
            };
          }
        }

        const rawList = extractJobsArray(res);
        const normalized = rawList.map(normalizeJob).filter(Boolean);

        if (process.env.NODE_ENV !== "production" && normalized.length === 0) {
          // eslint-disable-next-line no-console
          console.warn("[MTN-Jobs] extracted 0 jobs. Raw keys:", {
            hasData: !!res?.data,
            keys: res?.data ? Object.keys(res.data) : [],
          });
        }

        if (mounted) {
          setJobs(normalized);
          setErrMsg("");
        }
      } catch (error) {
        if (process.env.NODE_ENV !== "production") {
          // eslint-disable-next-line no-console
          console.error("[MTN-Jobs] failed to load:", {
            status: error?.response?.status,
            data: error?.response?.data,
            message: error?.message,
          });
          if (typeof window !== "undefined") {
            window.__MTN_JOBS_DEBUG_ERROR__ = {
              at: new Date().toISOString(),
              errorStatus: error?.response?.status,
              errorData: error?.response?.data,
              errorMessage: error?.message,
            };
          }
        }

        let friendly =
          "We couldn’t load the current vacancies. Please try again in a moment.";
        if (error?.code === "ECONNABORTED") {
          friendly =
            "The jobs service is taking longer than expected. Please refresh to try again.";
        }

        if (mounted) setErrMsg(friendly);
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900 flex flex-col">
      <main className="flex-grow">
        <JobListingTemplate
          jobs={jobs}
          loading={loading}
          errorMessage={errMsg}
        />
      </main>
      <Footer />
    </div>
  );
};

export default JobListingPage;
